import { useState } from "react"

import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"

import RenderLocation from "@/components/RenderLocation.tsx"
import { Button } from "@/components/ui/button.tsx"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible.tsx"
import WeatherDropdown from "@/components/WeatherDropdown.tsx"

import DailyDisplay from "./DailyDisplay.tsx"
import FlowRateDisplay from "./FlowRateDisplay.tsx"
import FlowRateGauge from "./FlowRateGauge.tsx"
import MonthlyDisplay from "./MonthlyDisplay.tsx"

import type { SummaryRowConfig } from "../types/summaryRowTypes.ts"

// Short-form site names shown below the `sm` breakpoint (mobile).
const SHORT_LOCATION_NAMES: Record<number, string> = {
  2: "BCO", // BC Organics
  4: "PLTH", // Plumb Thicket
  5: "LWRN", // Lawrence
}

function SummaryTitle({
  location,
  title,
  titleShort,
}: {
  location?: number
  title?: string
  titleShort?: string
}) {
  const shortFromLocation = location !== undefined ? SHORT_LOCATION_NAMES[location] : undefined
  const fullNode = title ?? (location !== undefined ? <RenderLocation id={location} /> : null)
  const shortNode = titleShort ?? shortFromLocation ?? fullNode

  return (
    <>
      <span className="hidden @md/summary:inline">{fullNode}</span>
      <span className="inline @md/summary:hidden">{shortNode}</span>
    </>
  )
}

export default function SummaryRow({
  location,
  config,
  title,
  titleShort,
}: {
  location: number
  config?: SummaryRowConfig
  title?: string
  titleShort?: string
}) {
  const [open, setOpen] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1280px)").matches,
  )
  const [viewIndex, setViewIndex] = useState(0)

  // Placeholder card: no config, just a titled empty card.
  if (!config) {
    return (
      <div className="@container/summary flex flex-col gap-4 p-4 bg-muted border rounded-md shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-semibold text-2xl shrink-0 whitespace-nowrap">
            <SummaryTitle location={location} title={title} titleShort={titleShort} />
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center min-h-[200px] text-muted-foreground text-sm">
          No data available
        </div>
      </div>
    )
  }

  const views = [
    {
      key: "gauge",
      render: () => <FlowRateGauge fetcher={config.flowRateFetcher} location={location} />,
    },
    {
      key: "live",
      render: () => <FlowRateDisplay location={location} fetcher={config.flowRateFetcher} />,
    },
    {
      key: "daily",
      render: () => (
        <DailyDisplay
          fetcher={config.dailyFetcher}
          location={location}
          flareUnit={location == 2 ? "MMBtu" : "MSCF"}
        />
      ),
    },
    {
      key: "monthly",
      render: () => <MonthlyDisplay fetcher={config.monthlyFetcher} location={location} />,
    },
  ]

  const prevView = () => setViewIndex((i) => (i - 1 + views.length) % views.length)
  const nextView = () => setViewIndex((i) => (i + 1) % views.length)

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="@container/summary flex flex-col gap-2 p-4 bg-muted border rounded-md shadow-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-2xl shrink-0 whitespace-nowrap">
          <SummaryTitle location={location} title={title} titleShort={titleShort} />
        </h2>
        <div className="flex items-center gap-1 shrink-0">
          {location !== undefined && <WeatherDropdown locationId={location} />}
          {!open && (
            <>
              <Button variant="ghost" size="icon" aria-label="Previous metric" onClick={prevView}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Next metric" onClick={nextView}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          )}
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={open ? "Hide metrics" : "Show metrics"}>
              <ChevronDown
                className={`h-5 w-5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              />
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>

      {!open && <div key={views[viewIndex].key}>{views[viewIndex].render()}</div>}

      <CollapsibleContent className="flex flex-col gap-1.5">
        {views.map((v) => (
          <div key={v.key}>{v.render()}</div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}
